// src/lib/learning/progress.js

import { matchPath } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { slugFor } from "./lessons";

function normalizeQuestKey(qid) {
  if (!qid) return null;

  const value = String(qid).toLowerCase();

  if (
    value === "financial-foundations" ||
    value === "quest-1" ||
    value === "q1" ||
    value === "1"
  ) {
    return "1";
  }

  if (value === "quest-2" || value === "q2" || value === "2") {
    return "2";
  }

  if (value === "quest-3" || value === "q3" || value === "3") {
    return "3";
  }

  return String(qid);
}

// Parses the /play route + page
export function derivePlayCtxFromPath(pathname) {
  const match = matchPath("/play/:questId/:subquestId/:levelKey/*", pathname);

  if (!match) {
    return null;
  }

  const { questId, subquestId, levelKey } = match.params;
  const qKey = normalizeQuestKey(questId);
  const slug = slugFor(qKey, subquestId, levelKey);

  const segments = pathname.split("/").filter(Boolean);
  const pageKey = segments[segments.length - 1] || "welcome";

  return {
    questId: qKey,
    subquestId,
    levelKey,
    slug,
    pageKey,
  };
}

/**
 * Upsert furthest level progress only.
 *
 * Important:
 * This file does NOT award XP.
 * Optional totalXp is only a progress-dashboard snapshot value.
 * Lesson XP is saved by LevelResult.jsx through upsertLessonProgress().
 */
export async function upsertLevelProgress({
  questId,
  subquestId,
  levelKey,
  totalXp,
}) {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) throw error;
  if (!user) return null;

  const payload = {
    user_id: user.id,
    quest_id: String(questId),
    subquest_id: String(subquestId),
    level_key: String(levelKey),
    updated_at: new Date().toISOString(),
  };

  if (totalXp != null) {
    const parsedTotalXp = Number(totalXp);

    if (Number.isFinite(parsedTotalXp)) {
      payload.total_xp = parsedTotalXp;
    }
  }

  const { data, error: upsertErr } = await supabase
    .from("user_level_progress")
    .upsert(payload, {
      onConflict: "user_id",
    })
    .select("*")
    .maybeSingle();

  if (upsertErr) throw upsertErr;

  return data;
}

export async function markPageStarted(_ctx) {
  // Intentionally no-op.
}

export async function markPageCompleted(ctx, _pct = 100, totalXp) {
  const { questId, subquestId, levelKey } = ctx;

  return upsertLevelProgress({
    questId,
    subquestId,
    levelKey,
    totalXp,
  });
}

export function goToWithProgress(
  navigate,
  ctx,
  currentPageKey,
  nextSegment,
  pct = 100
) {
  markPageCompleted(
    {
      ...ctx,
      pageKey: currentPageKey,
    },
    pct
  ).finally(() => {
    navigate(nextSegment);
  });
}